/**
 * World settings: visual detail, density, performance preference and automatic adaptation,
 * advanced graphics options (each automatic or pinned by hand), background life, motion, and
 * credits. Audio-specific choices live in the player's gear.
 */
import { useState, useSyncExternalStore } from 'react';
import { AUTO_GRAPHICS, hasOverrides, QUALITY_LADDER, type GraphicsOverrides } from '../framework/quality/profiles';
import { BUILD_STAMP } from './build';
import type { AppController, AppSnapshot } from './controller';
import { Button, Section, Segmented, Sheet, Toggle } from './ui/controls';

type Option<K extends keyof GraphicsOverrides> = { value: GraphicsOverrides[K]; label: string };

/** The advanced options, in the order shown, with plain labels and what each does. */
const ADVANCED: Array<{ [K in keyof GraphicsOverrides]: { key: K; label: string; hint: string; options: Array<Option<K>> } }[keyof GraphicsOverrides]> = [
  {
    key: 'resolution',
    label: 'Resolution',
    hint: 'Native matches your screen. Above it draws extra pixels for smoother edges, and is very demanding.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: '50', label: '50%' },
      { value: '75', label: '75%' },
      { value: '100', label: 'Native' },
      { value: '125', label: '125%' },
      { value: '150', label: '150%' },
      { value: '200', label: '200%' },
    ],
  },
  {
    key: 'frameRate',
    label: 'Frame rate',
    hint: 'Frames per second. Your screen’s own rate may be 120 or more on some displays.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: '30', label: '30' },
      { value: '60', label: '60' },
      { value: 'max', label: 'Screen’s own' },
    ],
  },
  {
    key: 'shadows',
    label: 'Shadows',
    hint: 'Sharpness and softness of sun and moon shadows.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'ultra', label: 'Ultra' },
    ],
  },
  {
    key: 'lightShafts',
    label: 'Light shafts',
    hint: 'Sunbeams and mist in the air. One of the heaviest effects.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'off', label: 'Off' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ],
  },
  {
    key: 'ambientOcclusion',
    label: 'Soft contact shadows',
    hint: 'Shading where things meet the ground and each other (ambient occlusion).',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'off', label: 'Off' },
      { value: 'on', label: 'On' },
    ],
  },
  {
    key: 'depthOfField',
    label: 'Background blur',
    hint: 'The camera’s shallow focus. Subtle or Off shows more of the far wood: deer, birds on the branch.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'off', label: 'Off' },
      { value: 'subtle', label: 'Subtle' },
      { value: 'full', label: 'Full' },
    ],
  },
  {
    key: 'bloom',
    label: 'Glow',
    hint: 'A soft glow around bright light (bloom).',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'off', label: 'Off' },
      { value: 'on', label: 'On' },
    ],
  },
  {
    key: 'smoothing',
    label: 'Edge smoothing',
    hint: 'Steadies fine moss, fur and leaf edges over time. Off is a little sharper but shimmers.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'off', label: 'Off' },
      { value: 'on', label: 'On' },
    ],
  },
  {
    key: 'furMoss',
    label: 'Fur and moss depth',
    hint: 'Layers in the animals’ fur and the moss. Ultra is for strong graphics cards.',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'ultra', label: 'Ultra' },
    ],
  },
];

export function SettingsSheet({ app, snap, open, onClose }: { app: AppController; snap: AppSnapshot; open: boolean; onClose: () => void }) {
  const p = useSyncExternalStore(app.prefs.subscribe, () => app.prefs.get());
  const v = p.visual;
  const level = QUALITY_LADDER[snap.qualityLevel];
  const g = v.graphics;
  const pinned = hasOverrides(g);
  const [advanced, setAdvanced] = useState(pinned);
  const [inviting, setInviting] = useState(false);
  return (
    <Sheet open={open} onClose={onClose} title="Settings">
      <Section title="Performance" hint="Balanced suits most devices. Battery saver renders at 30 frames per second.">
        <Segmented
          label="Performance preference"
          value={v.preference}
          options={[
            { value: 'battery', label: 'Battery saver' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'high', label: 'Highest' },
          ]}
          onChange={(x) => app.prefs.update((d) => (d.visual.preference = x))}
        />
        <Toggle
          label="Automatic performance"
          description={v.autoPerformance ? `Adjusting to keep motion smooth (now: ${level?.name ?? ''}).` : 'Fixed detail; may stutter on slower devices.'}
          checked={v.autoPerformance}
          onChange={(x) => app.prefs.update((d) => (d.visual.autoPerformance = x))}
        />
      </Section>
      <Section title="Visual detail" hint="Resolution, shadows, light shafts, moss and fur depth, and small particles. Ultra is for strong graphics cards.">
        <Segmented
          label="Visual detail"
          value={v.detail}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
            { value: 'ultra', label: 'Ultra' },
          ]}
          onChange={(x) => app.prefs.update((d) => (d.visual.detail = x))}
        />
        <Toggle
          label="Advanced graphics"
          description={pinned ? 'Some options are set by hand; the rest still adjust automatically.' : 'Set individual options by hand.'}
          checked={advanced}
          onChange={setAdvanced}
        />
        {advanced && (
          <div className="gbr-advanced">
            {ADVANCED.map((o) => (
              <div key={o.key} className="gbr-advanced-row">
                <span className="gbr-advanced-label">{o.label}</span>
                <Segmented
                  label={o.label}
                  wrap={o.options.length > 5}
                  value={g[o.key]}
                  options={o.options as Array<{ value: string; label: string }>}
                  onChange={(x) => app.prefs.update((d) => ((d.visual.graphics as Record<string, string>)[o.key] = x))}
                />
                <span className="gbr-hint">{o.hint}</span>
              </div>
            ))}
            <div className="gbr-row">
              <span className="gbr-hint">{pinned ? 'Options set by hand hold even when automatic performance would lower them, so a slower device may stutter.' : 'Everything is automatic.'}</span>
              <Button variant="chip" disabled={!pinned} onClick={() => app.prefs.update((d) => (d.visual.graphics = { ...AUTO_GRAPHICS }))}>
                Reset to automatic
              </Button>
            </div>
            <Toggle
              label="Show frame rate"
              description="A small readout in the corner: frames per second and the picture’s size."
              checked={v.showFrameRate}
              onChange={(x) => app.prefs.update((d) => (d.visual.showFrameRate = x))}
            />
          </div>
        )}
      </Section>
      <Section title="World density" hint="How many residents visit, how lush the foliage is, and how busy the background feels.">
        <Segmented
          label="World density"
          value={v.density}
          options={[
            { value: 'low', label: 'Sparse' },
            { value: 'normal', label: 'Normal' },
            { value: 'lush', label: 'Lush' },
          ]}
          onChange={(x) => app.prefs.update((d) => (d.visual.density = x))}
        />
        <Toggle
          label="Background wildlife"
          description="Deer, birds and the owl in the wood beyond. Turned off, any there leave in their own time."
          checked={v.wildlife}
          onChange={(x) => app.prefs.update((d) => (d.visual.wildlife = x))}
        />
        <Toggle
          label="Falling leaves"
          description="Now and then a leaf drifting down."
          checked={v.fallingLeaves}
          onChange={(x) => app.prefs.update((d) => (d.visual.fallingLeaves = x))}
        />
        {snap.geometryPending && (
          <div className="gbr-row">
            <span className="gbr-hint">Some changes rebuild the habitat and apply when you next enter.</span>
            <Button variant="chip" onClick={() => void app.applyGeometryNow()}>
              Apply now
            </Button>
          </div>
        )}
      </Section>
      {snap.visitors.length > 0 && (
        <Section title="Visitors">
          <Toggle
            label="Invite a visitor"
            description="Ask for a creature: it comes as it naturally would, within a few seconds. Outside its hours the time of day moves there first."
            checked={inviting}
            onChange={setInviting}
          />
          {inviting && (
            <div className="gbr-advanced">
              {(['resident', 'background'] as const).map((group) => (
                <div key={group} className="gbr-visitors" role="group" aria-label={group === 'resident' ? 'In the habitat' : 'In the wood beyond'}>
                  <span className="gbr-advanced-label">{group === 'resident' ? 'In the habitat' : 'In the wood beyond'}</span>
                  {snap.visitors
                    .filter((x) => x.group === group)
                    .map((x) => (
                      <div key={x.id} className="gbr-visitor">
                        <span className="gbr-visitor-name">{x.label}</span>
                        <span className="gbr-hint gbr-visitor-note">{x.note ?? ''}</span>
                        <Button variant="chip" disabled={x.state !== 'ready'} aria-label={`Invite: ${x.label}`} onClick={() => app.inviteVisitor(x.id)}>
                          Invite
                        </Button>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
      <Section title="Motion">
        <Segmented
          label="Reduce motion"
          value={p.a11y.reducedMotion}
          options={[
            { value: 'system', label: 'System' },
            { value: 'on', label: 'Reduce' },
            { value: 'off', label: 'Full' },
          ]}
          onChange={(x) => app.prefs.update((d) => (d.a11y.reducedMotion = x))}
        />
        <span className="gbr-hint">Reduce removes camera drift and eases transitions; residents still move.</span>
      </Section>
      <Section title="About">
        <p className="gbr-hint">
          Glass Box Relaxation · Mosslight 1.0. Every habitat, creature, texture and interim music track is original and generated by this project. Built with the PlayCanvas engine (MIT), React (MIT), Fraunces and Inter (SIL Open Font License).
        </p>
        {!app.prefs.persistent && <p className="gbr-hint">Preferences cannot be saved in this browser mode; they last for this visit only.</p>}
        <p className="gbr-hint">{BUILD_STAMP}</p>
      </Section>
    </Sheet>
  );
}
